import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Upload, CheckCircle, Briefcase, Phone, Mail, MapPin, FileText, Building2, CreditCard, User, X } from "lucide-react";
import { API_URL, LOGO_URL, axios } from "../constants";
import ShopLocationPicker from "../components/vendor/ShopLocationPicker";

// NOTE: InputField is defined at MODULE scope (not inside the component) so React
// keeps the <input> mounted across parent re-renders. Previously this lived inside
// VendorRegistration() which caused the typing bug — every keystroke dropped focus
// because the function identity changed on each render and React fully remounted
// the input. (See audit report iter179 for the regression history.)
const InputField = ({ icon: Icon, label, field, type = "text", placeholder, required, form, handleInput }) => (
  <div>
    <label className="text-sm font-medium text-[#1E1B4B] mb-1 flex items-center gap-1.5">
      <Icon size={13} className="text-[#E83E8C]" /> {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={form[field]}
      onChange={(e) => handleInput(field, type === "number" ? parseInt(e.target.value) || 0 : e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
    />
  </div>
);

// Full bilingual (EN + हिंदी) copy for the DoozyGo Partner Agreement.
// Lives at module scope so React re-renders don't rebuild the object every tick.
// Keys MUST match the `policyAccepted` state field names so each checkbox
// toggles the correct consent flag regardless of the active language.
const VENDOR_AGREEMENT_SECTIONS = [
  {
    key: "terms",
    en: {
      title: "1. Terms of Service",
      body: "By registering as a DoozyGo® vendor partner, you agree to provide professional event decoration and related services as per the bookings assigned to you. You acknowledge that DoozyGo® acts as an aggregator platform connecting customers with service providers. DoozyGo® reserves the right to assign, reassign, or cancel assignments based on operational needs. All communications with customers must go through DoozyGo®'s official channels.",
      consent: "I accept the Terms of Service",
    },
    hi: {
      title: "1. सेवा की शर्तें",
      body: "DoozyGo® वेंडर पार्टनर के रूप में पंजीकरण करके, आप आपको सौंपी गई बुकिंग के अनुसार पेशेवर इवेंट डेकोरेशन और संबंधित सेवाएं प्रदान करने के लिए सहमत हैं। आप स्वीकार करते हैं कि DoozyGo® ग्राहकों को सेवा प्रदाताओं से जोड़ने वाला एक एग्रीगेटर प्लेटफ़ॉर्म है। परिचालन आवश्यकताओं के आधार पर DoozyGo® को असाइनमेंट देने, पुनः सौंपने या रद्द करने का अधिकार है। ग्राहकों के साथ सभी संचार DoozyGo® के आधिकारिक चैनलों के माध्यम से होना चाहिए।",
      consent: "मैं सेवा की शर्तें स्वीकार करता/करती हूँ",
    },
  },
  {
    key: "quality",
    en: {
      title: "2. Quality Standards Agreement",
      body: "You commit to maintaining DoozyGo®'s quality standards at all times. This includes: arriving at venues on time (minimum 2 hours before event), using clean and high-quality materials, following the setup guidelines provided for each package, taking before/after photographs of every setup, and ensuring the venue is left clean after setup. Failure to meet quality standards may result in reduced quality scores, payment deductions, or suspension from the platform.",
      consent: "I agree to maintain DoozyGo quality standards",
    },
    hi: {
      title: "2. गुणवत्ता मानकों का अनुबंध",
      body: "आप हर समय DoozyGo® के गुणवत्ता मानकों को बनाए रखने के लिए प्रतिबद्ध हैं। इसमें शामिल हैं: समय पर स्थान पर पहुँचना (कार्यक्रम से कम से कम 2 घंटे पहले), स्वच्छ और उच्च-गुणवत्ता वाली सामग्री का उपयोग करना, प्रत्येक पैकेज के लिए दिए गए सेटअप दिशानिर्देशों का पालन करना, हर सेटअप की पहले/बाद की तस्वीरें लेना, और सेटअप के बाद स्थान को साफ़ छोड़ना। गुणवत्ता मानकों का पालन न करने पर गुणवत्ता स्कोर कम हो सकता है, भुगतान में कटौती हो सकती है, या प्लेटफ़ॉर्म से निलंबन हो सकता है।",
      consent: "मैं DoozyGo के गुणवत्ता मानकों को बनाए रखने के लिए सहमत हूँ",
    },
  },
  {
    key: "conduct",
    en: {
      title: "3. Code of Conduct",
      body: "As a DoozyGo® partner, you represent our brand at customer venues. You must maintain professional behavior, wear clean and presentable attire, communicate politely with customers, and respect their property and privacy. Harassment, discrimination, intoxication at work, or any form of misconduct will result in immediate termination from the platform and potential legal action. You must not solicit customers directly or share your personal contact details to bypass DoozyGo.",
      consent: "I agree to the Code of Conduct",
    },
    hi: {
      title: "3. आचार संहिता",
      body: "DoozyGo® पार्टनर के रूप में, आप ग्राहक के स्थान पर हमारे ब्रांड का प्रतिनिधित्व करते हैं। आपको पेशेवर व्यवहार बनाए रखना, साफ़ और सम्मानजनक पोशाक पहनना, ग्राहकों से विनम्रता से बात करना, और उनकी संपत्ति व निजता का सम्मान करना होगा। उत्पीड़न, भेदभाव, कार्य के दौरान नशा, या किसी भी प्रकार का कदाचार प्लेटफ़ॉर्म से तत्काल निष्कासन और संभावित कानूनी कार्रवाई का कारण बनेगा। आप सीधे ग्राहकों से संपर्क नहीं कर सकते या DoozyGo को बायपास करने के लिए अपने व्यक्तिगत संपर्क विवरण साझा नहीं कर सकते।",
      consent: "मैं आचार संहिता से सहमत हूँ",
    },
  },
  {
    key: "payment",
    en: {
      title: "4. Payment Terms",
      body: "Payment for completed jobs will be processed as per the agreed payment cycle (weekly/bi-weekly/monthly). DoozyGo® retains a platform commission on each booking. Payment will only be released after the quality checklist is completed and no customer complaints are pending. In case of cancellations by you, a penalty may be deducted from future payments. All payments will be made via bank transfer or UPI to the account details provided.",
      consent: "I accept the Payment Terms",
    },
    hi: {
      title: "4. भुगतान शर्तें",
      body: "पूरे किए गए कार्यों के लिए भुगतान सहमत भुगतान चक्र (साप्ताहिक/द्वि-साप्ताहिक/मासिक) के अनुसार संसाधित किया जाएगा। DoozyGo® प्रत्येक बुकिंग पर प्लेटफ़ॉर्म कमीशन रखता है। भुगतान तभी जारी किया जाएगा जब गुणवत्ता चेकलिस्ट पूरी हो जाए और कोई ग्राहक शिकायत लंबित न हो। आपके द्वारा रद्द करने की स्थिति में, भविष्य के भुगतानों से जुर्माना काटा जा सकता है। सभी भुगतान बैंक ट्रांसफर या UPI के माध्यम से दिए गए खाते के विवरण पर किए जाएंगे।",
      consent: "मैं भुगतान शर्तें स्वीकार करता/करती हूँ",
    },
  },
  {
    key: "data",
    en: {
      title: "5. Data Privacy & Confidentiality",
      body: "You consent to DoozyGo® collecting, storing, and processing your personal and business information for platform operations. Customer information shared with you for job assignments is strictly confidential and must not be stored, shared, or used for any purpose outside of the assigned job. DoozyGo® may share your profile information (name, service type, rating) on the marketplace. Location data may be collected during active jobs for quality assurance purposes.",
      consent: "I consent to the Data Privacy policy",
    },
    hi: {
      title: "5. डेटा गोपनीयता और गोपनीयता",
      body: "आप प्लेटफ़ॉर्म संचालन के लिए DoozyGo® द्वारा आपकी व्यक्तिगत और व्यावसायिक जानकारी एकत्र करने, संग्रहीत करने और संसाधित करने के लिए सहमति देते हैं। कार्य असाइनमेंट के लिए आपके साथ साझा की गई ग्राहक जानकारी पूरी तरह से गोपनीय है और इसे असाइन किए गए कार्य के बाहर किसी भी उद्देश्य के लिए संग्रहीत, साझा या उपयोग नहीं किया जाना चाहिए। DoozyGo® आपकी प्रोफ़ाइल जानकारी (नाम, सेवा प्रकार, रेटिंग) को मार्केटप्लेस पर साझा कर सकता है। गुणवत्ता आश्वासन उद्देश्यों के लिए सक्रिय कार्यों के दौरान स्थान डेटा एकत्र किया जा सकता है।",
      consent: "मैं डेटा गोपनीयता नीति से सहमत हूँ",
    },
  },
];

const DOC_TYPES = [
  { value: "pan", label: "PAN Card", required: true },
  { value: "aadhar", label: "Aadhar Card", required: true },
  { value: "gst_certificate", label: "GST Certificate", required: false },
  { value: "cancel_check", label: "Cancelled Cheque", required: true },
  { value: "shop_interior", label: "Shop Interior Photo", required: true, kind: "photo" },
  { value: "shop_exterior", label: "Shop Exterior Photo", required: true, kind: "photo" },
  { value: "shop_board", label: "Shop Name Board Photo", required: true, kind: "photo" },
  { value: "other", label: "Other Document", required: false },
];

export default function VendorRegistration() {
  const { magicToken } = useParams();
  const [step, setStep] = useState(1);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [vendorId, setVendorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [magicPrefilled, setMagicPrefilled] = useState(false);
  const [magicLinkInvalid, setMagicLinkInvalid] = useState(false);

  // Phone OTP verification (phase 1 — required before advancing to step 2)
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState("");
  const [verifiedPhone, setVerifiedPhone] = useState("");

  const [form, setForm] = useState({
    name: "", phone: "", alternate_phone: "", email: "", service_type: "",
    service_area: [], experience_years: 0, about: "",
    pricing_range: "", bank_name: "", bank_account_number: "",
    bank_ifsc: "", bank_account_holder: "", upi_id: "", gst_number: ""
  });

  // Shop location — chosen via ShopLocationPicker (Google Maps). Shape:
  // { lat, lng, accuracy, captured_at, address }
  const [shopLocation, setShopLocation] = useState(null);

  // OTP resend cooldown — 30s window so legit retries don't require a full page reload
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => setOtpCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  const [documents, setDocuments] = useState([]);
  // pendingDocs holds File objects selected locally BEFORE the final submit.
  // Shape: { [doc_type]: File }. These are uploaded AFTER /api/vendors/apply
  // returns a vendor_id (the backend requires it to attach docs).
  const [pendingDocs, setPendingDocs] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [policyAccepted, setPolicyAccepted] = useState({
    terms: false, quality: false, conduct: false, payment: false, data: false
  });
  // Agreement language toggle — iter186. English stays the default so the
  // existing audit trail / legal copy is unchanged for current vendors; the
  // Hindi version is a full translation of the same 5 sections.
  const [agreementLang, setAgreementLang] = useState("en");

  useEffect(() => {
    axios.get(`${API_URL}/api/vendors/service-types`)
      .then(res => {
        setServiceTypes(res.data.service_types || []);
        setServiceAreas(res.data.service_areas || []);
      }).catch(() => {});
  }, []);

  // Magic-link prefill — admin-issued one-time link auto-fills the form (iter148)
  useEffect(() => {
    if (!magicToken) return;
    axios.get(`${API_URL}/api/vendors/magic-link/${magicToken}`)
      .then((res) => {
        const d = res.data || {};
        setForm(prev => ({
          ...prev,
          name: d.name || prev.name,
          phone: d.phone || prev.phone,
          email: d.email || prev.email,
          service_type: d.service_type || prev.service_type,
          service_area: (d.service_area && d.service_area.length) ? d.service_area : prev.service_area,
        }));
        setMagicPrefilled(true);
      })
      .catch(() => setMagicLinkInvalid(true));
  }, [magicToken]);

  const handleInput = (field, value) => {
    // Alternate mobile must be numeric and 10 digits max.
    if (field === "alternate_phone") {
      value = String(value || "").replace(/\D/g, "").slice(0, 10);
    }
    setForm(prev => ({ ...prev, [field]: value }));
    // Any change to phone invalidates the existing OTP verification
    if (field === "phone") {
      setPhoneVerified(false);
      setOtpSent(false);
      setOtpCode("");
      setPhoneVerificationToken("");
      setVerifiedPhone("");
    }
  };

  const sendOtp = async () => {
    setError("");
    if (!form.phone || form.phone.length < 10) {
      setError("Enter a valid 10-digit phone number first");
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API_URL}/api/vendors/auth/send-otp`, {
        phone: form.phone, purpose: "registration",
      });
      setOtpSent(true);
      setOtpCooldown(30);  // 30s before resend is allowed again
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP");
    } finally { setOtpLoading(false); }
  };

  const verifyOtp = async () => {
    setError("");
    if (!otpCode || otpCode.length < 4) {
      setError("Enter the 6-digit OTP");
      return;
    }
    setOtpLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/vendors/auth/verify-otp`, {
        phone: form.phone, code: otpCode, purpose: "registration",
      });
      setPhoneVerificationToken(r.data.phone_verification_token || "");
      setVerifiedPhone(form.phone);
      setPhoneVerified(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired OTP");
    } finally { setOtpLoading(false); }
  };

  const toggleArea = (area) => {
    setForm(prev => ({
      ...prev,
      service_area: prev.service_area.includes(area)
        ? prev.service_area.filter(a => a !== area)
        : [...prev.service_area, area]
    }));
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.name || !form.phone || !form.service_type) {
      setError("Please fill in Name, Phone, and Service Type");
      setStep(1); return;
    }
    if (form.phone.length < 10) {
      setError("Please enter a valid phone number"); setStep(1); return;
    }
    if (!phoneVerified || form.phone !== verifiedPhone) {
      setError("Please verify your phone with the OTP before submitting"); setStep(1); return;
    }
    if (form.alternate_phone && form.alternate_phone.length !== 10) {
      setError("Alternate Mobile Number must be exactly 10 digits"); setStep(1); return;
    }
    if (!shopLocation) {
      setError("Please pin your Shop Location on the map in Step 1"); setStep(1); return;
    }
    const missingRequired = DOC_TYPES.filter(d => d.required && !pendingDocs[d.value]);
    if (missingRequired.length) {
      setError(`Please upload: ${missingRequired.map(d => d.label).join(", ")}`); setStep(3); return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/vendors/apply`, {
        ...form,
        shop_location: shopLocation,
        policy_accepted: policyAccepted,
        policy_accepted_at: new Date().toISOString(),
        phone_verification_token: phoneVerificationToken,
      });
      const vid = res.data.vendor_id;
      setVendorId(vid);

      // Upload the docs collected in Step 3 (now that we have vendor_id).
      // Object storage occasionally returns transient 500s on the first POST
      // (observed as `[STORAGE] put retry 1/2` on the backend) — a single
      // network/server blip used to drop the file into the "retry from Vendor
      // Portal" bucket on the success page, even though the user had selected
      // the file correctly. Add bounded client-side retries so transient
      // failures self-heal before we surface them to the vendor.
      const entries = Object.entries(pendingDocs);
      setUploadProgress({ done: 0, total: entries.length });
      setUploading(true);
      const uploadOne = async (docType, file) => {
        const MAX_ATTEMPTS = 3;
        let lastErr = null;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("doc_type", docType);
            fd.append("doc_name", file.name);
            await axios.post(`${API_URL}/api/vendors/${vid}/documents`, fd, { timeout: 45000 });
            return { ok: true };
          } catch (e) {
            lastErr = e;
            // Only retry on transient server/network errors — not on 4xx
            // (which indicate bad file / unsupported type / auth problems
            // that won't recover on their own).
            const status = e?.response?.status;
            const isTransient = !status || status >= 500 || status === 408 || status === 429;
            if (!isTransient || attempt === MAX_ATTEMPTS) break;
            const backoff = 400 * Math.pow(2, attempt - 1); // 400ms, 800ms, 1600ms
            await new Promise((r) => setTimeout(r, backoff));
          }
        }
        return { ok: false, err: lastErr };
      };

      for (let i = 0; i < entries.length; i++) {
        const [docType, file] = entries[i];
        const result = await uploadOne(docType, file);
        if (result.ok) {
          setDocuments((prev) => [...prev, { type: docType, name: file.name, uploaded: true }]);
        } else {
          // Don't fail the whole submission — surface a hint and let partner
          // re-upload from the Vendor Portal.
          setDocuments((prev) => [...prev, { type: docType, name: file.name, uploaded: false, error: true }]);
        }
        setUploadProgress({ done: i + 1, total: entries.length });
      }
      setUploading(false);

      setStep(6);
      if (magicToken) {
        axios.post(`${API_URL}/api/vendors/magic-link/${magicToken}/consume`, { vendor_id: vid })
          .catch(() => { /* ignore — still have the vendor row */ });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const selectDoc = (docType, file) => {
    if (!file) return;
    setPendingDocs(prev => ({ ...prev, [docType]: file }));
  };
  const removeDoc = (docType) => {
    setPendingDocs(prev => {
      const next = { ...prev };
      delete next[docType];
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="vendor-registration-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E1B4B] to-[#312E81] text-white">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/" className="p-2 hover:bg-white/10 rounded-xl transition-colors"><ChevronLeft size={20} /></Link>
            <img src={LOGO_URL} alt="DoozyGo" className="h-8 w-auto bg-white rounded-lg px-2 py-1" />
          </div>
          <h1 className="text-2xl font-bold">Become a DoozyGo Partner</h1>
          <p className="text-indigo-200 text-sm mt-1">Join our network of party setup professionals</p>
        </div>
      </div>

      {/* Progress — 5 interactive steps + a final success state */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step >= s ? 'bg-[#E83E8C] text-white' : 'bg-gray-200 text-gray-400'
              }`}>{step > s ? <CheckCircle size={16} /> : s}</div>
              {s < 5 && <div className={`flex-1 h-1 rounded-full ${step > s ? 'bg-[#E83E8C]' : 'bg-gray-200'}`}></div>}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between mt-1 text-[10px] sm:text-xs text-[#475569]">
          <span>Basic Info</span>
          <span>Bank</span>
          <span>Documents</span>
          <span>Agreement</span>
          <span>Review</span>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 pb-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4 flex items-center gap-2" data-testid="vendor-error">
            <X size={14} /> {error}
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="vendor-step-1">
            <h2 className="text-lg font-bold text-[#1E1B4B]">Basic Information</h2>
            <InputField icon={User} label="Full Name" field="name" placeholder="Your full name" required form={form} handleInput={handleInput} />

            {/* Phone + OTP gate */}
            <div data-testid="vendor-otp-gate">
              <label className="text-sm font-medium text-[#1E1B4B] mb-1 flex items-center gap-1.5">
                <Phone size={13} className="text-[#E83E8C]" /> Phone Number <span className="text-red-400">*</span>
                {phoneVerified && form.phone === verifiedPhone && (
                  <span className="ml-auto flex items-center gap-0.5 text-[11px] font-bold text-emerald-600" data-testid="vendor-phone-verified">
                    <CheckCircle size={12} /> Verified
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleInput("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  disabled={phoneVerified && form.phone === verifiedPhone}
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm disabled:bg-gray-50"
                  data-testid="vendor-phone-input"
                />
                {!otpSent && !phoneVerified && (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpLoading || form.phone.length < 10}
                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 hover:shadow-md transition-all whitespace-nowrap"
                    data-testid="vendor-send-otp-btn"
                  >
                    {otpLoading ? "..." : "Send OTP"}
                  </button>
                )}
                {phoneVerified && form.phone === verifiedPhone && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoneVerified(false);
                      setOtpSent(false);
                      setOtpCode("");
                      setPhoneVerificationToken("");
                      setVerifiedPhone("");
                    }}
                    className="px-3 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200"
                    data-testid="vendor-change-phone-btn"
                  >
                    Change
                  </button>
                )}
              </div>
              {otpSent && !phoneVerified && (
                <div className="mt-2 flex gap-2" data-testid="vendor-otp-input-row">
                  <input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit OTP"
                    className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm font-mono tracking-widest text-center"
                    data-testid="vendor-otp-input"
                  />
                  <button
                    type="button"
                    onClick={verifyOtp}
                    disabled={otpLoading || otpCode.length < 4}
                    className="px-4 py-2.5 rounded-xl bg-[#E83E8C] text-white text-xs font-bold disabled:opacity-50 hover:bg-[#d1366f]"
                    data-testid="vendor-verify-otp-btn"
                  >
                    {otpLoading ? "..." : "Verify"}
                  </button>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpCooldown > 0 || otpLoading}
                    className="text-[10px] text-[#E83E8C] font-bold hover:underline px-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="vendor-resend-otp-btn"
                  >
                    {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend"}
                  </button>
                </div>
              )}
              {!phoneVerified && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  We'll send a one-time code to confirm it's really you. Required for your safety.
                </p>
              )}
            </div>

            <InputField icon={Mail} label="Email" field="email" type="email" placeholder="you@email.com" form={form} handleInput={handleInput} />

            <div>
              <label className="text-sm font-medium text-[#1E1B4B] mb-1 flex items-center gap-1.5">
                <Briefcase size={13} className="text-[#E83E8C]" /> Service Type <span className="text-red-400">*</span>
              </label>
              <select value={form.service_type} onChange={(e) => handleInput("service_type", e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
                data-testid="vendor-service-type">
                <option value="">Select service type</option>
                {serviceTypes.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-1.5">
                <MapPin size={13} className="text-[#E83E8C]" /> Service Areas
              </label>
              <div className="flex flex-wrap gap-2">
                {serviceAreas.map(area => (
                  <button key={area} type="button" onClick={() => toggleArea(area)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      form.service_area.includes(area)
                        ? 'bg-[#E83E8C] text-white border-[#E83E8C]'
                        : 'bg-white text-[#475569] border-gray-200 hover:border-[#E83E8C]'
                    }`}>{area}</button>
                ))}
              </div>
            </div>

            <InputField icon={Briefcase} label="Years of Experience" field="experience_years" type="number" placeholder="0" form={form} handleInput={handleInput} />
            <InputField icon={CreditCard} label="Pricing Range" field="pricing_range" placeholder="e.g., Rs 2,000 - Rs 10,000 per event" form={form} handleInput={handleInput} />

            <div>
              <label className="text-sm font-medium text-[#1E1B4B] mb-1">About You / Your Services</label>
              <textarea value={form.about} onChange={(e) => handleInput("about", e.target.value)}
                placeholder="Tell us about your services, specialties, and experience..."
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm h-24 resize-none" />
            </div>

            {/* Alternate Mobile — moved from Step 2 (iter183). 10-digit numeric only. */}
            <div>
              <label className="text-sm font-medium text-[#1E1B4B] mb-1 flex items-center gap-1.5">
                <Phone size={13} className="text-[#E83E8C]" /> Alternate Mobile Number
                <span className="text-[10px] text-[#94a3b8] font-normal ml-1">(optional)</span>
              </label>
              <input
                type="tel"
                value={form.alternate_phone}
                onChange={(e) => handleInput("alternate_phone", e.target.value)}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none text-sm"
                data-testid="vendor-alt-phone-input"
              />
              {form.alternate_phone && form.alternate_phone.length !== 10 && (
                <p className="text-[11px] text-amber-600 mt-1">Must be exactly 10 digits.</p>
              )}
            </div>

            {/* Shop Location — Google Maps picker (search + draggable marker + GPS). */}
            <div className="pt-2 border-t border-gray-100">
              <label className="text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-1.5">
                <MapPin size={13} className="text-[#E83E8C]" /> Shop Location <span className="text-red-400">*</span>
              </label>
              <ShopLocationPicker value={shopLocation} onChange={setShopLocation} />
            </div>

            <button
              onClick={() => {
                if (!form.name || !form.phone || !form.service_type) { setError("Please fill in all required fields"); return; }
                if (!phoneVerified || form.phone !== verifiedPhone) { setError("Please verify your phone with OTP before continuing"); return; }
                if (form.alternate_phone && form.alternate_phone.length !== 10) { setError("Alternate Mobile Number must be exactly 10 digits"); return; }
                if (!shopLocation) { setError("Please pin your Shop Location on the map"); return; }
                setError(""); setStep(2);
              }}
              disabled={!phoneVerified || form.phone !== verifiedPhone}
              className="w-full py-3 bg-[#E83E8C] text-white rounded-xl font-semibold hover:bg-[#d1366f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="vendor-next-step-1">
              Next: Bank Details
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="vendor-step-2">
            <h2 className="text-lg font-bold text-[#1E1B4B]">Bank Details</h2>
            <p className="text-sm text-[#94a3b8]">Used for your weekly payout. All details are encrypted at rest.</p>

            <InputField icon={Building2} label="Bank Name" field="bank_name" placeholder="e.g., HDFC Bank" form={form} handleInput={handleInput} />
            <InputField icon={User} label="Account Holder Name" field="bank_account_holder" placeholder="Name as per bank account" form={form} handleInput={handleInput} />
            <InputField icon={CreditCard} label="Account Number" field="bank_account_number" placeholder="Account number" form={form} handleInput={handleInput} />
            <InputField icon={Building2} label="IFSC Code" field="bank_ifsc" placeholder="e.g., HDFC0001234" form={form} handleInput={handleInput} />
            <InputField icon={CreditCard} label="UPI ID" field="upi_id" placeholder="yourname@upi" form={form} handleInput={handleInput} />
            <InputField icon={FileText} label="GST Number" field="gst_number" placeholder="Optional" form={form} handleInput={handleInput} />

            <div className="flex gap-3">
              <button onClick={() => setStep(1)}
                className="flex-1 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                Back
              </button>
              <button onClick={() => { setError(""); setStep(3); }}
                className="flex-1 py-3 bg-[#E83E8C] text-white rounded-xl font-semibold hover:bg-[#d1366f] transition-colors"
                data-testid="vendor-next-step-2">
                Next: Documents
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Documents (moved before Agreement, iter183). Files are
            cached locally; they upload after final Submit returns vendor_id. */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="vendor-step-3">
            <h2 className="text-lg font-bold text-[#1E1B4B]">Upload Documents</h2>
            <p className="text-sm text-[#94a3b8]">Items marked <span className="text-red-500 font-bold">*</span> are mandatory. Shop photos help us verify your business.</p>

            <div className="space-y-3">
              {DOC_TYPES.map(dt => {
                const file = pendingDocs[dt.value];
                const isPhoto = dt.kind === "photo";
                return (
                  <div key={dt.value} className={`flex items-center gap-3 p-3 rounded-xl border ${dt.required && !file ? "bg-amber-50/50 border-amber-200" : "bg-[#FFFAF5] border-gray-100"}`} data-testid={`doc-row-${dt.value}`}>
                    <FileText size={18} className={file ? "text-green-500" : "text-[#94a3b8]"} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1E1B4B] flex items-center gap-1 flex-wrap">
                        {dt.label}
                        {dt.required && <span className="text-red-500" title="Required">*</span>}
                        {isPhoto && <span className="text-[9px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full font-bold">PHOTO</span>}
                      </p>
                      {file && <p className="text-xs text-green-600 truncate">{file.name}</p>}
                      {dt.required && !file && <p className="text-[10px] text-amber-700">Mandatory for approval</p>}
                    </div>
                    {file ? (
                      <button type="button" onClick={() => removeDoc(dt.value)} className="text-xs text-[#E83E8C] font-semibold hover:underline" data-testid={`doc-remove-${dt.value}`}>
                        Remove
                      </button>
                    ) : (
                      <label className="px-3 py-1.5 bg-[#E83E8C] text-white text-xs rounded-lg cursor-pointer hover:bg-[#d1366f] transition-colors flex items-center gap-1" data-testid={`doc-pick-${dt.value}`}>
                        <Upload size={12} /> Choose
                        <input type="file" className="hidden"
                          accept={isPhoto ? "image/jpeg,image/png,image/webp" : ".pdf,.jpg,.jpeg,.png,.doc,.docx"}
                          capture={isPhoto ? "environment" : undefined}
                          onChange={(e) => selectDoc(dt.value, e.target.files[0])} />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(2)}
                className="flex-1 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                Back
              </button>
              <button
                onClick={() => {
                  const missing = DOC_TYPES.filter(d => d.required && !pendingDocs[d.value]);
                  if (missing.length) { setError(`Please upload: ${missing.map(d => d.label).join(", ")}`); return; }
                  setError(""); setStep(4);
                }}
                className="flex-1 py-3 bg-[#E83E8C] text-white rounded-xl font-semibold hover:bg-[#d1366f] transition-colors"
                data-testid="vendor-next-step-3">
                Next: Agreement
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Agreement (moved after Documents, iter183) */}
        {step === 4 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="vendor-step-4">
            {(() => {
              // Local hook invocation isn't possible inside this JSX branch,
              // so language state is lifted to a sibling closure via a ref.
              return null;
            })()}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-[#1E1B4B]">
                  {agreementLang === "hi" ? "DoozyGo पार्टनर अनुबंध" : "DoozyGo Partner Agreement"}
                </h2>
                <p className="text-sm text-[#94a3b8]">
                  {agreementLang === "hi"
                    ? "कृपया सभी शर्तें पढ़ें और स्वीकार करें।"
                    : "Please read and accept all terms to proceed."}
                </p>
              </div>
              <div
                className="inline-flex rounded-xl bg-gray-100 p-0.5 text-xs font-semibold"
                role="group"
                aria-label="Agreement language"
                data-testid="vendor-agreement-lang-toggle"
              >
                <button
                  type="button"
                  onClick={() => setAgreementLang("en")}
                  className={`px-3 py-1.5 rounded-lg transition ${agreementLang === "en" ? "bg-white text-[#E83E8C] shadow-sm" : "text-[#475569]"}`}
                  aria-pressed={agreementLang === "en"}
                  data-testid="vendor-agreement-lang-en"
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setAgreementLang("hi")}
                  className={`px-3 py-1.5 rounded-lg transition ${agreementLang === "hi" ? "bg-white text-[#E83E8C] shadow-sm" : "text-[#475569]"}`}
                  aria-pressed={agreementLang === "hi"}
                  data-testid="vendor-agreement-lang-hi"
                  lang="hi"
                >
                  हिंदी
                </button>
              </div>
            </div>

            {VENDOR_AGREEMENT_SECTIONS.map((sec) => {
              const copy = sec[agreementLang] || sec.en;
              return (
                <div className="border border-gray-100 rounded-xl p-4 space-y-3" key={sec.key}>
                  <div
                    className="bg-[#FFFAF5] rounded-lg p-3 max-h-36 overflow-y-auto text-xs text-[#475569] leading-relaxed"
                    lang={agreementLang}
                  >
                    <p className="font-bold text-[#1E1B4B] mb-2">{copy.title}</p>
                    <p>{copy.body}</p>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer" data-testid={`policy-${sec.key}`}>
                    <input
                      type="checkbox"
                      checked={!!policyAccepted[sec.key]}
                      onChange={e => setPolicyAccepted(p => ({ ...p, [sec.key]: e.target.checked }))}
                      className="mt-0.5 rounded border-gray-300 text-[#E83E8C] focus:ring-[#E83E8C]"
                    />
                    <span className="text-xs text-[#1E1B4B] font-medium" lang={agreementLang}>{copy.consent}</span>
                  </label>
                </div>
              );
            })}

            {!Object.values(policyAccepted).every(Boolean) && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2" lang={agreementLang}>
                {agreementLang === "hi"
                  ? "आगे बढ़ने के लिए कृपया सभी अनुबंधों को स्वीकार करें।"
                  : "Please accept all agreements to continue."}
              </p>
            )}

            <div className="flex gap-3">
              <button onClick={() => setStep(3)}
                className="flex-1 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                {agreementLang === "hi" ? "वापस" : "Back"}
              </button>
              <button
                onClick={() => { setError(""); setStep(5); }}
                disabled={!Object.values(policyAccepted).every(Boolean)}
                className="flex-1 py-3 bg-[#E83E8C] text-white rounded-xl font-semibold hover:bg-[#d1366f] transition-colors disabled:opacity-50"
                data-testid="vendor-next-step-4">
                Next: Review
              </button>
            </div>
          </div>
        )}

        {/* Step 5 — Review before Registration (new, iter183).
            Shows every entered value with inline "Edit" buttons that jump
            back to the exact step. Final Submit button lives here. */}
        {step === 5 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5" data-testid="vendor-step-5">
            <div>
              <h2 className="text-lg font-bold text-[#1E1B4B]">Review Your Details</h2>
              <p className="text-sm text-[#94a3b8]">Please verify everything is correct. You can edit any section before submitting.</p>
            </div>

            {/* Basic Info summary */}
            <div className="rounded-xl border border-gray-100 p-4" data-testid="review-basic">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-[#1E1B4B]">Basic Information</p>
                <button type="button" onClick={() => setStep(1)} className="text-xs text-[#E83E8C] font-semibold hover:underline" data-testid="review-edit-basic">Edit</button>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div><dt className="text-[#94a3b8]">Full Name</dt><dd className="text-[#1E1B4B] font-medium">{form.name || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Phone (verified)</dt><dd className="text-[#1E1B4B] font-medium">{form.phone || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Alternate Mobile</dt><dd className="text-[#1E1B4B] font-medium">{form.alternate_phone || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Email</dt><dd className="text-[#1E1B4B] font-medium break-all">{form.email || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Service Type</dt><dd className="text-[#1E1B4B] font-medium">{form.service_type || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Experience</dt><dd className="text-[#1E1B4B] font-medium">{form.experience_years || 0} yrs</dd></div>
                <div className="sm:col-span-2"><dt className="text-[#94a3b8]">Service Areas</dt><dd className="text-[#1E1B4B] font-medium">{(form.service_area || []).join(", ") || "—"}</dd></div>
                <div className="sm:col-span-2"><dt className="text-[#94a3b8]">Pricing Range</dt><dd className="text-[#1E1B4B] font-medium">{form.pricing_range || "—"}</dd></div>
                <div className="sm:col-span-2">
                  <dt className="text-[#94a3b8]">Shop GPS</dt>
                  <dd className="text-[#1E1B4B] font-medium">
                    {shopLocation ? `${shopLocation.lat.toFixed(6)}, ${shopLocation.lng.toFixed(6)} (±${shopLocation.accuracy}m)` : "—"}
                  </dd>
                </div>
                {form.about && (
                  <div className="sm:col-span-2"><dt className="text-[#94a3b8]">About</dt><dd className="text-[#1E1B4B]">{form.about}</dd></div>
                )}
              </dl>
            </div>

            {/* Bank summary */}
            <div className="rounded-xl border border-gray-100 p-4" data-testid="review-bank">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-[#1E1B4B]">Bank Details</p>
                <button type="button" onClick={() => setStep(2)} className="text-xs text-[#E83E8C] font-semibold hover:underline" data-testid="review-edit-bank">Edit</button>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div><dt className="text-[#94a3b8]">Bank Name</dt><dd className="text-[#1E1B4B] font-medium">{form.bank_name || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Account Holder</dt><dd className="text-[#1E1B4B] font-medium">{form.bank_account_holder || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">Account Number</dt><dd className="text-[#1E1B4B] font-medium">{form.bank_account_number ? `•••• ${form.bank_account_number.slice(-4)}` : "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">IFSC</dt><dd className="text-[#1E1B4B] font-medium">{form.bank_ifsc || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">UPI</dt><dd className="text-[#1E1B4B] font-medium">{form.upi_id || "—"}</dd></div>
                <div><dt className="text-[#94a3b8]">GST</dt><dd className="text-[#1E1B4B] font-medium">{form.gst_number || "—"}</dd></div>
              </dl>
            </div>

            {/* Documents summary */}
            <div className="rounded-xl border border-gray-100 p-4" data-testid="review-docs">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-[#1E1B4B]">Documents ({Object.keys(pendingDocs).length})</p>
                <button type="button" onClick={() => setStep(3)} className="text-xs text-[#E83E8C] font-semibold hover:underline" data-testid="review-edit-docs">Edit</button>
              </div>
              <ul className="space-y-1 text-xs">
                {DOC_TYPES.map(dt => {
                  const f = pendingDocs[dt.value];
                  return (
                    <li key={dt.value} className="flex items-center gap-2">
                      {f ? <CheckCircle size={12} className="text-emerald-500" /> : <X size={12} className={dt.required ? "text-red-400" : "text-gray-300"} />}
                      <span className="text-[#1E1B4B] font-medium">{dt.label}{dt.required && <span className="text-red-500">*</span>}</span>
                      <span className="text-[#94a3b8] truncate">{f ? f.name : (dt.required ? "missing" : "skipped")}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Agreement summary */}
            <div className="rounded-xl border border-gray-100 p-4" data-testid="review-agreement">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-[#1E1B4B]">Agreement</p>
                <button type="button" onClick={() => setStep(4)} className="text-xs text-[#E83E8C] font-semibold hover:underline" data-testid="review-edit-agreement">Edit</button>
              </div>
              <p className="text-xs text-emerald-700 flex items-center gap-1">
                <CheckCircle size={12} /> All 5 policies accepted
              </p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(4)}
                className="flex-1 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || uploading}
                className="flex-1 py-3 bg-[#E83E8C] text-white rounded-xl font-semibold hover:bg-[#d1366f] transition-colors disabled:opacity-50"
                data-testid="vendor-submit-btn">
                {submitting ? "Submitting..." : (uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : "Submit Registration")}
              </button>
            </div>
          </div>
        )}

        {/* Step 6 — Success (post-submit, docs already uploaded in handleSubmit) */}
        {step === 6 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="vendor-step-6">
            <div className="text-center mb-6">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#1E1B4B]">Application Submitted!</h2>
              <p className="text-sm text-[#475569] mt-1">Your ID: <strong className="text-[#E83E8C]">{vendorId}</strong></p>
              <p className="text-sm text-[#94a3b8] mt-2">Our team will review your application and documents, and contact you within 48 hours.</p>
            </div>

            {documents.length > 0 && (
              <div className="rounded-xl border border-gray-100 p-4 mb-4">
                <p className="text-sm font-bold text-[#1E1B4B] mb-2">Documents received</p>
                <ul className="space-y-1 text-xs">
                  {documents.map((d) => (
                    <li key={`${d.type}-${d.name}`} className="flex items-center gap-2">
                      {d.uploaded ? <CheckCircle size={12} className="text-emerald-500" /> : <X size={12} className="text-amber-500" />}
                      <span className="text-[#1E1B4B]">{d.name}</span>
                      {!d.uploaded && <span className="text-[11px] text-amber-700">(retry from Vendor Portal)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <p className="text-sm text-green-700 font-medium">What happens next?</p>
              <ol className="text-xs text-green-600 mt-2 space-y-1 list-decimal list-inside">
                <li>Our team reviews your application & documents</li>
                <li>You receive a WhatsApp/call for onboarding discussion</li>
                <li>Once approved, you start receiving job assignments</li>
                <li>Payments are processed as per the agreed cycle</li>
              </ol>
            </div>

            <Link to="/" className="block text-center mt-4 text-sm text-[#E83E8C] hover:underline">
              Return to DoozyGo® Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
